import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, FileJson, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";

import { AuditApi, type AuditEntry } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { timeAgo } from "@/lib/utils";

const ALL = "__all__";

export function AuditPage() {
  const [q, setQ] = useState("");
  const [action, setAction] = useState<string>(ALL);
  const [entityType, setEntityType] = useState<string>(ALL);
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["audit", q, action, entityType],
    queryFn: () =>
      AuditApi.list({
        q: q || undefined,
        action: action === ALL ? undefined : action,
        entityType: entityType === ALL ? undefined : entityType,
        limit: 200,
      }),
    refetchInterval: 15_000,
  });
  const { data: facets } = useQuery({
    queryKey: ["audit-facets"],
    queryFn: () => AuditApi.facets(),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ScrollText className="size-5 text-primary" />
          {fa.audit.title}
        </h1>
        <p className="text-sm text-muted-foreground">{fa.audit.subtitle}</p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex flex-wrap items-center gap-3">
            <span>آخرین رویدادها</span>
            <div className="ms-auto flex flex-wrap items-center gap-2">
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="h-9 w-44 lat-num">
                  <SelectValue placeholder="action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>همه actionها</SelectItem>
                  {(facets?.actions ?? []).map((a) => (
                    <SelectItem key={a} value={a} className="lat-num">{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger className="h-9 w-44 lat-num">
                  <SelectValue placeholder="entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>همه entityها</SelectItem>
                  {(facets?.entityTypes ?? []).map((e) => (
                    <SelectItem key={e} value={e} className="lat-num">{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="w-56"
                placeholder="جستجو در action / entity / IP…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.empty}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>زمان</TableHead>
                  <TableHead>کاربر</TableHead>
                  <TableHead>action</TableHead>
                  <TableHead>entity</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead className="text-end">diff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.map((a) => <AuditRow key={a.id} a={a} onView={setOpen} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AuditDiffDialog entry={open} onOpenChange={(o) => !o && setOpen(null)} />
    </div>
  );
}

function AuditRow({ a, onView }: { a: AuditEntry; onView: (e: AuditEntry) => void }) {
  const hasDiff = a.diff != null && (typeof a.diff !== "object" || Object.keys(a.diff as object).length > 0);
  return (
    <TableRow
      className={hasDiff ? "cursor-pointer hover:bg-muted/50" : ""}
      onClick={() => hasDiff && onView(a)}
    >
      <TableCell className="text-xs text-muted-foreground">{timeAgo(a.createdAt)}</TableCell>
      <TableCell>{a.userEmail ?? a.apiKeyName ?? "system"}</TableCell>
      <TableCell><Badge variant="outline" className="lat-num">{a.action}</Badge></TableCell>
      <TableCell className="text-xs lat-num">
        {a.entityType}{a.entityId ? `:${a.entityId.slice(0, 12)}` : ""}
      </TableCell>
      <TableCell className="text-xs lat-num">{a.ip ?? "—"}</TableCell>
      <TableCell className="text-end">
        {hasDiff ? (
          <FileJson className="size-3.5 text-muted-foreground inline" />
        ) : (
          <span className="text-xs text-muted-foreground/40">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

// Pretty-print the diff JSON. Audit rows can carry anything from a tiny
// `{ keys: [...] }` to a full migrate-disk result, so we render syntax-aware
// with a scrollable code block and a copy button.
function AuditDiffDialog({
  entry,
  onOpenChange,
}: {
  entry: AuditEntry | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = entry != null;
  const json = entry ? JSON.stringify(entry.diff, null, 2) : "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="size-4 text-primary" />
            <span className="lat-num font-mono">{entry?.action}</span>
            <Badge variant="outline" className="lat-num text-[10px]">{entry?.entityType}</Badge>
          </DialogTitle>
          <DialogDescription>
            <span dir="ltr" className="lat-num font-mono text-[11px]">
              {entry?.userEmail ?? entry?.apiKeyName ?? "system"} · {entry?.ip ?? "—"} ·{" "}
              {entry?.createdAt
                ? new Date(entry.createdAt).toISOString().replace("T", " ").slice(0, 19)
                : ""}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 end-2 z-10"
            onClick={() => {
              void navigator.clipboard.writeText(json);
              toast.success("JSON کپی شد");
            }}
          >
            <CopyIcon className="size-3.5" />
          </Button>
          <pre
            dir="ltr"
            className="max-h-96 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs font-mono lat-num leading-relaxed"
          >
            {json}
          </pre>
        </div>
        {entry?.userAgent && (
          <div className="text-[11px] text-muted-foreground lat-num truncate" dir="ltr" title={entry.userAgent}>
            UA: {entry.userAgent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
